import { httpsCallable } from "firebase/functions";
import * as fb from "../../firebase";
import {
  CLEAR_CURRENT_DISPLAYED_USER,
  CLEAR_DATA,
  CLEAR_DISPLAYED_TEAM,
  MyTeamMember,
  MyTeamState,
  SET_IS_LOADING,
  SET_LEADER_TEAM_CACHE,
  sortableColumns,
  UPDATE_BREADCRUMBS,
  UPDATE_CURRENT_DISPLAYED_USER,
  UPDATE_DISPLAYED_TEAM,
  UPDATE_SORT_CONFIGURATION,
} from "./MyTeamInterfaces";
import { BasicResponse, DataResponse } from "functions/src/responseTypes";
import { ActionContext } from "vuex";
import { images } from "@/mixins/images";
import {
  PACKAGE_VISUAL_ORDER,
  productVisualId,
} from "@/components/common/productVisuals";

const initialState: MyTeamState = {
  displayedTeam: [],
  fullTeamCache: new Map(),
  currentDisplayedUser: "",
  breadcrumbs: [],
  isLoading: false,
  sortConfiguration: [],
};

const state: MyTeamState = { ...initialState };

// ---------------------------------------------------------------------------
// LA ORDENACIÓN DE «MI EQUIPO»
// ---------------------------------------------------------------------------
//
// POR QUÉ EL ORDEN «NO AGARRABA TODA LA LISTA». El comparador anterior sólo
// sabía comparar dos textos o dos números: tenía una rama para
// `typeof a === "string" && typeof b === "string"` y otra para el caso numérico,
// y NINGUNA RAMA MÁS. Si uno de los dos valores llegaba `null` —y en esta
// pantalla llegan nulos por todas partes, porque `MyTeamFunc.ts` normaliza cada
// número con `m.campo ? parseInt(m.campo) : null`— no entraba en ninguna de las
// dos, seguía al criterio siguiente y acababa devolviendo 0. Devolver 0 no es
// «no sé»: es «son iguales», así que esas filas se quedaban clavadas donde
// estuvieran mientras el resto se reordenaba a su alrededor. Con muchos nulos
// —el caso de `rank_priority`, que es nulo para todo el que no tiene rango— el
// efecto es exactamente el que se veía: media lista ordenada y media quieta.
//
// Aquí eso se parte en dos piezas con responsabilidades separadas:
//   · `sortValueFor`      — de qué valor se ordena cada columna (que no siempre
//                           es el valor que se pinta).
//   · `compareSortValues` — cómo se comparan dos valores ya normalizados,
//                           incluidos los vacíos.

/** Lo que sale de una celda una vez normalizada para ordenar. `null` = vacío. */
type SortValue = string | number | null;

/**
 * La meta de la beca. Al llegar a ella el socio ya la tiene conseguida y los
 * puntos de más dejan de significar nada, así que a partir de aquí todos valen
 * lo mismo y forman un solo bloque al ordenar.
 */
const SCHOLARSHIP_TARGET = 300;

/**
 * El orden de la ESCALERA de paquetes: Go → Fly → Win.
 *
 * Sale de `PACKAGE_VISUAL_ORDER`, que ya declara ese orden para los filtros y
 * las leyendas — no se vuelve a escribir aquí para que no puedan discrepar.
 * Ordenar por el id del paquete, que es lo que se hacía, daba el orden del
 * diccionario: `gtr-fly` < `gtr-go` < `gtr-win`, o sea Fly, Go y Win. Ese orden
 * no significa nada para nadie: pone el escalón de en medio delante del de
 * entrada sólo porque la «f» va antes que la «g».
 */
const PACKAGE_SORT_INDEX = new Map<string, number>(
  PACKAGE_VISUAL_ORDER.map((productId, index) => [productId, index])
);

/** ¿Este valor está vacío a efectos de ordenar? */
function isEmptySortValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    // Un `parseInt` de algo que no era un número devuelve `NaN`, y `NaN` rompe
    // cualquier comparación numérica en silencio: no es menor, ni mayor, ni
    // igual que nada. Se trata como lo que es, un hueco.
    (typeof value === "number" && !Number.isFinite(value))
  );
}

/**
 * EL VALOR POR EL QUE SE ORDENA CADA COLUMNA.
 *
 * Tres columnas no se ordenan por su valor crudo, y las tres tienen su motivo:
 *
 *  · `cmmi_end_date` guarda una FECHA, pero la columna sólo dice si el socio
 *    tiene el Acelerador o no. Ordenar por la fecha mezclaría «lo tiene desde
 *    hace mucho» con «lo tiene desde ayer» cuando lo que se busca es separar a
 *    los que lo tienen de los que no. Se reduce a 1/0.
 *
 *  · `scholarship_points` se agrupa a partir de la meta, ver `SCHOLARSHIP_TARGET`.
 *    El vacío cuenta como CERO y no como «meta alcanzada», que es lo que hacía
 *    antes: el backend convierte el 0 en `null`, así que un socio con cero
 *    puntos y uno sin beca llegan iguales, y la propia celda los pinta a los dos
 *    con los tres muñequitos apagados. El orden dice ahora lo mismo que el
 *    dibujo; antes los mandaba al final, junto a los que ya la habían conseguido.
 *
 *  · `current_package` se traduce a su escalón, ver `PACKAGE_SORT_INDEX`.
 *
 * El resto de columnas se ordena por su valor tal cual. Merece la pena señalar
 * que el orden por RANGO ya usaba el campo correcto y sigue usándolo:
 * `sortableColumns` sólo ofrece `rank_priority`, el número que fija la jerarquía,
 * y nunca `current_rank`, que es el id de texto («ELITE», «BOSS»…) y habría dado
 * un orden de diccionario sin ningún sentido de negocio. Lo que fallaba en el
 * orden por rango no era el campo: era que `rank_priority` llega nulo para todo
 * el que aún no tiene rango, y el comparador viejo se atragantaba con eso.
 */
function sortValueFor(
  member: MyTeamMember,
  column: sortableColumns
): SortValue {
  const rawValue = (member as unknown as Record<string, unknown>)[column];

  switch (column) {
    case "cmmi_end_date":
      return rawValue ? 1 : 0;

    case "scholarship_points": {
      if (isEmptySortValue(rawValue)) {
        return 0;
      }
      const points = Number(rawValue);
      return points >= SCHOLARSHIP_TARGET ? SCHOLARSHIP_TARGET : points;
    }

    case "current_package": {
      const productId = productVisualId(
        typeof rawValue === "string" ? rawValue : null
      );
      const index = PACKAGE_SORT_INDEX.get(productId);
      // «Sin paquete» y cualquier id que no sepamos colocar son un hueco, no un
      // escalón: se van al final igual que el resto de vacíos, en lugar de
      // colarse delante de Go o detrás de Win según cayera la dirección.
      return index === undefined ? null : index;
    }

    default:
      return isEmptySortValue(rawValue) ? null : (rawValue as string | number);
  }
}

/**
 * COMPARA DOS VALORES YA NORMALIZADOS.
 *
 * LOS VACÍOS VAN SIEMPRE AL FINAL, en las dos direcciones, y por eso su rama
 * está ANTES de aplicar el signo de la dirección. Es deliberado: quien ordena
 * «de mayor a menor» busca los valores más grandes arriba, y si los vacíos
 * siguieran la dirección se le llenaría la cabecera de filas en blanco justo
 * donde estaba mirando. Es la convención de cualquier hoja de cálculo.
 *
 * SE DEVUELVE -1/0/1 Y NUNCA UN BOOLEANO. Un comparador que devuelve `true`
 * ordena mal sin avisar: `true` se convierte en 1 y `false` en 0, o sea «este va
 * después» y «son iguales» — nunca puede decir «este va antes», así que la mitad
 * de los intercambios no se hacen. No es un fallo hipotético, es el error clásico
 * de escribir `(a, b) => a.campo > b.campo`.
 *
 * MEZCLA DE TIPOS: si los dos son números se restan; si no, se comparan como
 * texto con `localeCompare` en español, que ordena la ñ y los acentos donde un
 * lector español los espera —cosa que el `<` de JavaScript, que compara puntos
 * de código, no hace— y que con `numeric: true` sigue ordenando bien un texto
 * que en realidad es un número («10» después de «9», no antes).
 */
function compareSortValues(
  a: SortValue,
  b: SortValue,
  direction: "asc" | "desc"
): number {
  const aIsEmpty = a === null;
  const bIsEmpty = b === null;
  if (aIsEmpty && bIsEmpty) return 0;
  if (aIsEmpty) return 1;
  if (bIsEmpty) return -1;

  const directionSign = direction === "asc" ? 1 : -1;

  if (typeof a === "number" && typeof b === "number") {
    if (a === b) return 0;
    return a < b ? -directionSign : directionSign;
  }

  return (
    String(a).localeCompare(String(b), "es", {
      numeric: true,
      sensitivity: "base",
    }) * directionSign
  );
}

const mutations = {
  UPDATE_DISPLAYED_TEAM(
    state: MyTeamState,
    displayedTeam: Array<MyTeamMember>
  ) {
    state.displayedTeam = displayedTeam;
  },
  SET_LEADER_TEAM_CACHE(
    state: MyTeamState,
    teamInfo: {
      teamLeader: string;
      teamData: { uplines: Array<string>; leaderTeam: Array<MyTeamMember> };
    }
  ) {
    state.fullTeamCache.set(teamInfo.teamLeader, teamInfo.teamData);
  },
  UPDATE_CURRENT_DISPLAYED_USER(state: MyTeamState, username: string) {
    state.currentDisplayedUser = username;
  },
  UPDATE_BREADCRUMBS(state: MyTeamState, breadcrumbs: Array<string>) {
    state.breadcrumbs = breadcrumbs;
  },
  SET_IS_LOADING(state: MyTeamState, isLoading: boolean) {
    state.isLoading = isLoading;
  },
  CLEAR_DATA(state: MyTeamState) {
    initialState.fullTeamCache = new Map();
    state.fullTeamCache = new Map();
    state.breadcrumbs = [];
    state.displayedTeam = [];
    state.currentDisplayedUser = "";
    state.isLoading = false;
    Object.assign(state, initialState);
  },
  CLEAR_DISPLAYED_TEAM(state: MyTeamState) {
    state.displayedTeam = [];
  },
  CLEAR_FULL_TEAM_CACHE(state: MyTeamState) {
    state.fullTeamCache = new Map();
  },
  CLEAR_CURRENT_DISPLAYED_USER(state: MyTeamState) {
    state.currentDisplayedUser = "";
  },
  CLEAR_BREADCRUMBS(state: MyTeamState) {
    state.breadcrumbs = [];
  },
  UPDATE_SORT_CONFIGURATION(
    state: MyTeamState,
    sortConfiguration: Array<Map<sortableColumns, "asc" | "desc">>
  ) {
    state.sortConfiguration = sortConfiguration;
  },
};

const actions = {
  async doGetTeamForUser(
    context: ActionContext<MyTeamState, any>,
    params: { username: string; forceUpdate: boolean } = {
      username: "",
      forceUpdate: false,
    }
  ) {
    const { commit, dispatch, getters } = context;
    commit(SET_IS_LOADING, true);
    commit(CLEAR_DISPLAYED_TEAM);
    commit(CLEAR_CURRENT_DISPLAYED_USER);
    dispatch("locallyAdjustBreadcrumbs", params.username);

    let teamToDisplay:
      | { uplines: Array<string>; leaderTeam: Array<MyTeamMember> }
      | undefined = undefined;
    teamToDisplay = await getters.getCachedTeam(params.username);
    if (!teamToDisplay || params.forceUpdate === true) {
      // Here we will call the API to get the team for the given
      const getFullTeamForUser = httpsCallable(
        fb.functions,
        "MyTeam-GetFullTeamForUser"
      );
      await getFullTeamForUser({
        username: params.username,
        forceUpdate: params.forceUpdate,
      })
        .then(async (result) => {
          const response = result.data as BasicResponse;
          // console.log(response);
          if (response.OPCODE === "SUCCESS") {
            const data = (response as DataResponse<any>).data as {
              teamMembers: Array<MyTeamMember>;
              userUplines: Array<string>;
            };

            const profilePictures = data.teamMembers.map(async (member) => {
              member.profile_picture = await images.methods.getProfilePicture(
                member.pictureUrl
              );

              // Count how many downlines the user has
              // by counting how many times the user appears in the uplines array
              member.downlines = data.teamMembers.filter((m) =>
                m.uplines.includes(member._usernamePK)
              ).length;
            });

            await Promise.all(profilePictures).then(() => {
              teamToDisplay = {
                uplines: data.userUplines,
                leaderTeam: data.teamMembers,
              };
              commit(SET_LEADER_TEAM_CACHE, {
                teamLeader: params.username,
                teamData: teamToDisplay,
              });
            });
          } else {
            if (response.OPCODE === "ERROR") {
              throw new Error(response.message);
            }
          }
        })
        .catch(async (error) => {
          console.error(error);
          teamToDisplay = {
            uplines: await getters.getBreadcrumbs,
            leaderTeam: [],
          };
        });
    }
    commit(UPDATE_DISPLAYED_TEAM, teamToDisplay?.leaderTeam);
    commit(UPDATE_BREADCRUMBS, teamToDisplay?.uplines);
    commit(UPDATE_CURRENT_DISPLAYED_USER, params.username);
    dispatch("sortDisplayedTeam");
    commit(SET_IS_LOADING, false);
  },
  doRefreshTeamForUser({ dispatch }: ActionContext<MyTeamState, any>) {
    dispatch("doGetTeamForUser", {
      username: state.currentDisplayedUser,
      forceUpdate: true,
    });
  },
  locallyAdjustBreadcrumbs(
    { commit }: ActionContext<MyTeamState, any>,
    username: string
  ) {
    const index = state.breadcrumbs.indexOf(username);
    if (index > -1) {
      commit(UPDATE_BREADCRUMBS, state.breadcrumbs.slice(0, index + 1));
    } else {
      commit(UPDATE_BREADCRUMBS, [...state.breadcrumbs, username]);
    }
  },
  clearData({ commit }: any) {
    commit(CLEAR_DATA);
  },
  updateSortConfiguration(
    { commit, dispatch }: ActionContext<MyTeamState, any>,
    sortColumn: { column: sortableColumns; direction: "asc" | "desc" },
    executeSort: boolean = true
  ) {
    // Get the current sort configuration for the column
    const currentSortConfiguration = state.sortConfiguration.find((c) =>
      c.get(sortColumn.column)
    );

    if (!currentSortConfiguration) {
      // If there is no sort configuration, add it to the configuration preserving the rest
      const newSortConfiguration = new Map<sortableColumns, "asc" | "desc">();
      newSortConfiguration.set(sortColumn.column, sortColumn.direction);
      commit(UPDATE_SORT_CONFIGURATION, [
        ...state.sortConfiguration,
        newSortConfiguration,
      ]);
    } else if (
      // If the column is already in the configuration and the direction is the same, remove it preserving the rest
      currentSortConfiguration.get(sortColumn.column) === sortColumn.direction
    ) {
      const newSortConfiguration = state.sortConfiguration.filter(
        (c) => !c.get(sortColumn.column)
      );
      commit(UPDATE_SORT_CONFIGURATION, newSortConfiguration);
    } else {
      // La columna ya estaba, pero en la otra dirección: se le da la vuelta.
      //
      // Se construye un Map NUEVO en vez de hacer `c.set(...)` sobre el que ya
      // está en el estado. El `set` funcionaba —Vue 3 sí detecta la escritura en
      // un Map reactivo— pero era una escritura al estado hecha desde una acción
      // y por la puerta de atrás, sin pasar por la mutación: con Vuex en modo
      // estricto revienta, y para cualquiera que lea el historial de mutaciones
      // el cambio de dirección aparecía como un `UPDATE_SORT_CONFIGURATION` cuya
      // carga ya venía modificada de casa.
      const newSortConfiguration = state.sortConfiguration.map(
        (columnConfiguration) => {
          if (!columnConfiguration.get(sortColumn.column)) {
            return columnConfiguration;
          }
          return new Map<sortableColumns, "asc" | "desc">([
            [sortColumn.column, sortColumn.direction],
          ]);
        }
      );
      commit(UPDATE_SORT_CONFIGURATION, newSortConfiguration);
    }
    if (executeSort) {
      dispatch("sortDisplayedTeam");
    }
  },
  /**
   * ORDENA LA LISTA ENTERA, no lo que se ve.
   *
   * Se ordena SIEMPRE sobre `state.displayedTeam` completo, que es el equipo
   * entero tal y como lo devolvió la callable: esta pantalla no pagina ni
   * recorta nada, así que no hay ninguna «página actual» que pudiera ordenarse
   * por su cuenta. La sensación de que «el orden no agarraba toda la lista» no
   * venía de ahí sino del comparador: ver `compareSortValues`.
   *
   * Se ordena sobre una COPIA (`[...]`) y el resultado entra por una mutación.
   * No es un detalle de estilo: `Array.prototype.sort` reordena en el sitio, y
   * hacerlo directamente sobre el array reactivo del store dispararía el
   * re-render a mitad de la ordenación, con la lista en un estado intermedio.
   * La copia también es lo que deja intacta la copia del `fullTeamCache`, que
   * es de donde se recupera el orden natural aquí abajo.
   */
  sortDisplayedTeam({ commit, state }: ActionContext<MyTeamState, any>) {
    // SIN CRITERIOS SE VUELVE AL ORDEN NATURAL, que es el que trae el backend.
    // Antes esta rama no existía: al quitar la última pastilla de ordenación la
    // lista se quedaba con el último orden aplicado, así que la pantalla decía
    // «Ordenado por: (nada)» encima de una lista que seguía ordenada. El orden
    // de origen no se había perdido — está en el `fullTeamCache`, que nunca se
    // reordena justamente porque aquí se trabaja sobre una copia.
    if (state.sortConfiguration.length === 0) {
      const cachedTeam = state.fullTeamCache.get(state.currentDisplayedUser);
      if (cachedTeam) {
        commit(UPDATE_DISPLAYED_TEAM, [...cachedTeam.leaderTeam]);
      }
      return;
    }

    // Cada entrada de la configuración es un Map con UN solo par
    // columna→dirección, y el orden del array es el orden de prioridad: el
    // primer criterio que desempata gana.
    const criteria = state.sortConfiguration
      .map(
        (columnConfiguration) => Array.from(columnConfiguration.entries())[0]
      )
      .filter(
        (entry): entry is [sortableColumns, "asc" | "desc"] => entry != null
      );

    const sortedTeam = [...state.displayedTeam];
    sortedTeam.sort((a: MyTeamMember, b: MyTeamMember) => {
      for (const [column, direction] of criteria) {
        const result = compareSortValues(
          sortValueFor(a, column),
          sortValueFor(b, column),
          direction
        );
        if (result !== 0) {
          return result;
        }
      }
      // Empate real en todos los criterios. Se devuelve 0 y no un valor
      // inventado: `sort` es estable desde ES2019, así que las filas empatadas
      // conservan el orden que ya tenían en vez de barajarse en cada re-orden.
      return 0;
    });
    commit(UPDATE_DISPLAYED_TEAM, sortedTeam);
  },
};

const getters = {
  getDisplayedTeam: (state: MyTeamState) => state.displayedTeam,
  getIsLoading: (state: MyTeamState) => state.isLoading,
  getBreadcrumbs: (state: MyTeamState) => state.breadcrumbs,
  getCurrentDisplayedUser: (state: MyTeamState) => state.currentDisplayedUser,
  getCachedTeam: (state: MyTeamState) => (username: string) =>
    state.fullTeamCache.get(username),
  getSortConfiguration: (state: MyTeamState) => state.sortConfiguration,
  isSortedBy:
    (state: MyTeamState) =>
    (column: sortableColumns, direction: "asc" | "desc") =>
      state.sortConfiguration.some((c) => c.get(column) === direction),
  getColumnSortDirection: (state: MyTeamState) => (column: sortableColumns) => {
    const sortConfiguration = state.sortConfiguration.find((c) =>
      c.get(column)
    );
    return sortConfiguration ? sortConfiguration.get(column) : "";
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
